import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../provider/AuthContext';
import { useGetDevicesQuery } from '../provider/redux/query/device.service';

const RefreshIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <polyline points='23 4 23 10 17 10'></polyline>
    <polyline points='1 20 1 14 7 14'></polyline>
    <path d='M3.51 9a9 9 0 0 1 14.88-3.36L23 10'></path>
    <path d='M20.49 15a9 9 0 0 1-14.88 3.36L1 14'></path>
  </svg>
);

const LogoutIcon = () => (
  <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4'></path>
    <polyline points='16 17 21 12 16 7'></polyline>
    <line x1='21' y1='12' x2='9' y2='12'></line>
  </svg>
);

function formatTime(value) {
  const date = value ? new Date(value) : new Date();
  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(date);
}

function Navbar() {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();
  const { data, isFetching, refetch } = useGetDevicesQuery(undefined, {
    pollingInterval: 30000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: !isAuthenticated,
  });
  const [spinRefresh, setSpinRefresh] = useState(false);
  const spinTimeoutRef = useRef(null);

  const lastPushDisplay = useMemo(() => {
    const value = data?.data?.lastPush || data?.data?.lastHeartbeat || Date.now();
    return formatTime(value);
  }, [data]);

  const handleRefresh = () => {
    if (!isAuthenticated) return;
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('mm-refresh'));
    }
  };

  useEffect(() => {
    if (isFetching) {
      setSpinRefresh(true);
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
        spinTimeoutRef.current = null;
      }
      return;
    }
    if (spinRefresh) {
      spinTimeoutRef.current = setTimeout(() => {
        setSpinRefresh(false);
        spinTimeoutRef.current = null;
      }, 850);
    }
  }, [isFetching, spinRefresh]);

  useEffect(
    () => () => {
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onRefresh = () => {
      refetch();
    };
    window.addEventListener('mm-refresh', onRefresh);
    return () => window.removeEventListener('mm-refresh', onRefresh);
  }, [isAuthenticated, refetch]);

  return (
    <header className='topbar'>
      <Link className='brand' to='/' aria-label='Go to dashboard'>
        <span className='brand-icon'>
          <img src='/mm-logo.png' alt='Minute Marketing' className='brand-logo' />
          <span className='build-info'>{process.env.REACT_APP_BUILD || 'dev'}({process.env.REACT_APP_GIT_SHA || 'unknown'})</span>
        </span>
      </Link>

      <div className='top-actions'>
        <div className='last-push'>
          <div className='label'>Last Refresh</div>
          <div>{lastPushDisplay}</div>
        </div>
        <button className={`pill-btn ghost icon-only${spinRefresh ? ' is-refreshing' : ''}`} onClick={handleRefresh} title={isFetching ? 'Refreshing…' : 'Refresh data'} aria-label='Refresh data'>
          <RefreshIcon />
        </button>
        <button
          className='pill-btn icon-only logout-btn'
          onClick={() => {
            logout();
            navigate('/login');
          }}
          title='Logout'
          aria-label='Logout'
        >
          <LogoutIcon />
        </button>
      </div>
    </header>
  );
}

export default Navbar;
